import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface NoteSuite {
  id: string;
  contenu: string;
  created_at: string;
  medecins: { nom: string; prenom: string } | null;
}

interface NoteSectionProps {
  suiteId: string;
  medecinId: string;
  suiteTerminee: boolean;
}

const textareaClass = 'w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue focus:outline-none transition-colors resize-none text-sm bg-white';

export default function NoteSection({ suiteId, medecinId, suiteTerminee }: NoteSectionProps) {
  const [notes, setNotes] = useState<NoteSuite[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [contenu, setContenu] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadNotes = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('notes_suites')
        .select('id, contenu, created_at, medecins(nom, prenom)')
        .eq('suite_soin_id', suiteId)
        .order('created_at', { ascending: false });
      setNotes(data || []);
    } catch (err) {
      console.error('Erreur chargement des notes:', err);
    } finally {
      setLoading(false);
    }
  }, [suiteId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleSave = async () => {
    const texte = contenu.trim();
    if (!texte) {
      setErrorMsg('Écrivez une note avant de l\'enregistrer.');
      return;
    }
    setSaving(true);
    setErrorMsg(null);
    try {
      const { error } = await supabase.from('notes_suites').insert({
        suite_soin_id: suiteId,
        medecin_id: medecinId || null,
        contenu: texte,
      });
      if (error) throw error;
      setContenu('');
      setShowForm(false);
      loadNotes();
    } catch (err: any) {
      setErrorMsg(err.message || 'Erreur lors de l\'enregistrement de la note.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      const { error } = await supabase.from('notes_suites').delete().eq('id', id);
      if (error) throw error;
      loadNotes();
    } catch (err: any) {
      setErrorMsg(err.message || 'Erreur lors de la suppression de la note.');
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100 flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    Notes
        </h2>
        {!suiteTerminee && (
          <button
            onClick={() => { setShowForm(!showForm); setErrorMsg(null); }}
            className={`px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1.5 ${
              showForm ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-amber-600 text-white hover:bg-amber-700'
            }`}
          >
            <i className={showForm ? 'ri-close-line' : 'ri-add-line'}></i>
            {showForm ? 'Annuler' : 'Ajouter une note'}
          </button>
        )}
      </div>

      <div className="p-6">
        {errorMsg && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2 mb-4">
            <i className="ri-error-warning-line flex-shrink-0"></i>{errorMsg}
          </div>
        )}

        {showForm && (
          <div className="space-y-3 mb-5">
            <textarea
              value={contenu}
              onChange={(e) => setContenu(e.target.value)}
              rows={4}
              placeholder="Ajoutez ici toute information utile : observations, remarques, consignes particulières..."
              className={textareaClass}
            />
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Enregistrer la note
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-400 py-4 text-center">Chargement des notes...</p>
        ) : notes.length === 0 ? (
          <div className="text-center py-8">
            <div className="py-6 bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <i className="ri-sticky-note-line text-3xl text-gray-300"></i>
              <p className="text-sm text-gray-500 mt-2">Aucune note pour cette suite de soins.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((n) => (
              <div key={n.id} className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap flex-1">{n.contenu}</p>
                  {!suiteTerminee && (
                    <button
                      onClick={() => handleDelete(n.id)}
                      title="Supprimer cette note"
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-white transition-colors cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-delete-bin-6-line"></i>
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {n.medecins ? `${n.medecins.prenom} ${n.medecins.nom}` : 'Médecin'} ·{' '}
                  {new Date(n.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}{' '}
                  à {new Date(n.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}